import React from 'react';
import { AUDIO_STYLES } from '../../../services/veoService';

interface VeoAudioControlsProps {
  dialogueCue: string;
  onDialogueCueChange: (value: string) => void;
  soundEffects: string;
  onSoundEffectsChange: (value: string) => void;
  ambientNoise: string;
  onAmbientNoiseChange: (value: string) => void;
  audioStyle: string;
  onAudioStyleChange: (value: string) => void;
  isAudioEnabled: boolean;
}

const VeoAudioControls: React.FC<VeoAudioControlsProps> = ({
  dialogueCue,
  onDialogueCueChange,
  soundEffects,
  onSoundEffectsChange,
  ambientNoise,
  onAmbientNoiseChange,
  audioStyle,
  onAudioStyleChange,
  isAudioEnabled
}) => {
  if (!isAudioEnabled) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: 'rgba(107, 114, 128, 0.1)',
        borderRadius: '8px',
        border: '1px solid rgba(107, 114, 128, 0.3)',
        marginBottom: '20px'
      }}>
        <p style={{ 
          margin: 0, 
          fontSize: '14px', 
          color: '#9ca3af',
          textAlign: 'center'
        }}>
          🔇 Audio generation not available for Veo 2. 
          Select Veo 3 or Veo 3 Fast for native audio.
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h4 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: '600',
          color: '#fff'
        }}>
          🎵 Audio Generation
        </h4>
        <span style={{
          marginLeft: '8px',
          padding: '2px 8px',
          backgroundColor: '#10b981',
          color: '#fff',
          fontSize: '11px',
          borderRadius: '12px',
          fontWeight: '500'
        }}>
          Native Audio
        </span>
      </div>

      {/* Audio Style Selection */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#fff'
        }}>
          🎨 Audio Style:
        </label>
        <select
          value={audioStyle}
          onChange={(e) => onAudioStyleChange(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none'
          }}
        >
          <option value="">Select audio style...</option>
          {AUDIO_STYLES.map((style) => (
            <option key={style.id} value={style.id}>
              {style.name} - {style.description}
            </option>
          ))}
        </select>
      </div>

      {/* Dialogue Cue */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#fff'
        }}>
          💬 Dialogue Cue:
        </label>
        <textarea
          value={dialogueCue}
          onChange={(e) => onDialogueCueChange(e.target.value)}
          placeholder={`"Hello, how are you today?" she asked with a smile.`}
          maxLength={200}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginTop: '4px'
        }}>
          <span style={{ fontSize: '12px', color: '#ccc' }}>
            Use quotes for specific speech
          </span>
          <span style={{ 
            fontSize: '12px', 
            color: dialogueCue.length > 180 ? '#ef4444' : '#ccc'
          }}>
            {dialogueCue.length}/200
          </span>
        </div>
      </div>

      {/* Sound Effects */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#fff'
        }}>
          🔊 Sound Effects:
        </label>
        <textarea
          value={soundEffects}
          onChange={(e) => onSoundEffectsChange(e.target.value)}
          placeholder="footsteps on gravel, car engine starting, birds chirping"
          maxLength={150}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginTop: '4px'
        }}>
          <span style={{ fontSize: '12px', color: '#ccc' }}>
            Describe specific sounds and actions
          </span>
          <span style={{ 
            fontSize: '12px', 
            color: soundEffects.length > 130 ? '#ef4444' : '#ccc'
          }}>
            {soundEffects.length}/150
          </span>
        </div>
      </div>

      {/* Ambient Noise */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#fff'
        }}>
          🌿 Ambient Noise:
        </label>
        <textarea
          value={ambientNoise}
          onChange={(e) => onAmbientNoiseChange(e.target.value)}
          placeholder="busy café atmosphere, distant traffic, gentle rain"
          maxLength={100}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginTop: '4px'
        }}>
          <span style={{ fontSize: '12px', color: '#ccc' }}>
            Background environmental sounds
          </span>
          <span style={{ 
            fontSize: '12px', 
            color: ambientNoise.length > 80 ? '#ef4444' : '#ccc'
          }}>
            {ambientNoise.length}/100
          </span>
        </div>
      </div>

      {/* Audio Examples */}
      <div style={{
        padding: '12px',
        backgroundColor: 'rgba(16, 185, 129, 0.05)',
        borderRadius: '6px',
        border: '1px solid rgba(16, 185, 129, 0.2)'
      }}>
        <h5 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '13px', 
          fontWeight: '600',
          color: '#10b981'
        }}>
          💡 Audio Examples:
        </h5>
        <div style={{ fontSize: '12px', color: '#ccc', lineHeight: '1.4' }}>
          <p style={{ margin: '0 0 6px 0' }}>
            <strong>Dialogue:</strong> "Welcome to our store," the clerk said warmly.
          </p>
          <p style={{ margin: '0 0 6px 0' }}>
            <strong>Sound FX:</strong> phone ringing, keyboard typing, door creaking
          </p>
          <p style={{ margin: 0 }}>
            <strong>Ambient:</strong> coffee shop chatter, ocean waves, city traffic
          </p>
        </div>
      </div>
    </div>
  );
};

export default VeoAudioControls;